import { useState, useEffect } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

const CATEGORY_KEYS = {
  complaint:  { tKey: 'catComplaint',  color: '#FF1E6E' },
  suggestion: { tKey: 'catSuggestion', color: '#27E0FF' },
  praise:     { tKey: 'catPraise',     color: '#B6FF3C' },
  bug:        { tKey: 'catBug',        color: '#FFA500' },
  staff:      { tKey: 'catStaff',      color: '#9B5CFF' },
  equipment:  { tKey: 'catEquipment',  color: '#FFC53D' },
  other:      { tKey: 'catOther',      color: '#8892a0' },
};

const SOURCE = {
  mobile:      '📱',
  pc_launcher: '🖥️',
  admin:       '🎛️',
  web:         '🌐',
};

function fmt(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('uz-UZ', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
}

export default function Feedbacks() {
  const { t } = useLang();
  const getCat = key => {
    const ck = CATEGORY_KEYS[key] || CATEGORY_KEYS.other;
    return { ...ck, label: t(ck.tKey) };
  };

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [labId, setLabId] = useState('');
  const [status, setStatus] = useState('new');
  const [category, setCategory] = useState('');
  const [respondFor, setRespondFor] = useState(null);
  const [responseText, setResponseText] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (labId) q.set('lab_id', labId);
      if (status) q.set('status', status);
      if (category) q.set('category', category);
      const [i, s, l] = await Promise.all([
        api.get('/feedback/owner?' + q.toString()),
        api.get('/feedback/owner/stats'),
        api.get('/owner/labs'),
      ]);
      setItems(i.data || []);
      setStats(s.data);
      setLabs(l.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [labId, status, category]);

  const respond = async (id) => {
    try {
      await api.post(`/feedback/${id}/respond`, { response: responseText, status: 'resolved' });
      setRespondFor(null);
      setResponseText('');
      load();
    } catch (e) { alert(e.message); }
  };

  const setDone = async (id) => {
    try {
      await api.post(`/feedback/${id}/respond`, { status: 'resolved' });
      load();
    } catch (e) { alert(e.message); }
  };

  return (
    <div style={{ padding: 20 }}>
      <h1 style={{ marginBottom: 16, fontSize: 22 }}>{t('feedbacksTitle')}</h1>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
          <Stat title={t('fbStatTotal')}      value={stats.total} />
          <Stat title={t('fbStatusNew')}      value={stats.pending} color="#FFC53D" />
          <Stat title={t('fbStatComplaints')} value={stats.complaints} color="#FF1E6E" />
          <Stat title={t('fbStatSuggestions')} value={stats.suggestions} color="#27E0FF" />
          <Stat title={t('fbStatPraises')}    value={stats.praises} color="#B6FF3C" />
          <Stat title={t('fbStatBugs')}       value={stats.bugs} color="#FFA500" />
          <Stat title={t('fbStatAvgRating')}  value={stats.avg_rating ? `${Number(stats.avg_rating).toFixed(1)}⭐` : '—'} color="#FFD93D" />
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={labId} onChange={e => setLabId(e.target.value)}
                style={{ padding: 8, background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)' }}>
          <option value="">{t('fbAllClubs')}</option>
          {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)}
                style={{ padding: 8, background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)' }}>
          <option value="">{t('fbAllStatuses')}</option>
          <option value="new">{t('fbStatusNew')}</option>
          <option value="in_progress">{t('fbStatusInProgress')}</option>
          <option value="resolved">{t('fbStatusResolved')}</option>
        </select>
        <select value={category} onChange={e => setCategory(e.target.value)}
                style={{ padding: 8, background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)' }}>
          <option value="">{t('fbAllCategories')}</option>
          {Object.keys(CATEGORY_KEYS).map(k => <option key={k} value={k}>{t(CATEGORY_KEYS[k].tKey)}</option>)}
        </select>
      </div>

      {loading ? <div>{t('loading')}</div>
       : items.length === 0
        ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>{t('fbNoMessages')}</div>
        : items.map(f => {
          const cat = getCat(f.category);
          return (
            <div key={f.id} style={{
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderLeft: `3px solid ${cat.color}`,
              padding: 14,
              marginBottom: 10,
            }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 22 }}>{SOURCE[f.source] || '❓'}</span>
                <span style={{ padding: '3px 8px', background: `${cat.color}20`, color: cat.color,
                               border: `1px solid ${cat.color}`, fontSize: 11, fontWeight: 600, letterSpacing: 0.3 }}>
                  {cat.label}
                </span>
                {f.rating && <span style={{ color: '#FFC53D', fontSize: 14 }}>
                  {'⭐'.repeat(f.rating)}
                </span>}
                <span style={{ padding: '3px 8px', background: f.is_anonymous ? '#8892a020' : '#B6FF3C20',
                               color: f.is_anonymous ? '#8892a0' : '#B6FF3C',
                               border: `1px solid ${f.is_anonymous ? '#8892a0' : '#B6FF3C'}`,
                               fontSize: 11, fontWeight: 600 }}>
                  {f.is_anonymous ? t('fbAnonymous') : t('fbPublic')}
                </span>
                <span style={{ marginLeft: 'auto', color: 'var(--text2)', fontSize: 12 }}>{fmt(f.created_at)}</span>
              </div>
              {f.subject && <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{f.subject}</div>}
              <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{f.body}</div>
              {f.lab_name && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text2)' }}>
                {t('fbClubLabel')} {f.lab_name}
              </div>}
              {f.owner_response && (
                <div style={{ marginTop: 10, padding: 10, background: '#B6FF3C10', border: '1px solid #B6FF3C',
                              fontSize: 13, color: '#B6FF3C' }}>
                  {t('fbOwnerResponse')} {f.owner_response}
                </div>
              )}
              {!f.owner_ack_at && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <button onClick={() => setRespondFor(f.id)}
                          style={{ padding: '6px 14px', background: 'var(--accent)', border: 0,
                                   color: '#fff', fontSize: 12, cursor: 'pointer' }}>
                    {t('fbReply')}
                  </button>
                  <button onClick={() => setDone(f.id)}
                          style={{ padding: '6px 14px', background: 'transparent', border: '1px solid var(--border)',
                                   color: 'var(--text)', fontSize: 12, cursor: 'pointer' }}>
                    {t('fbMarkResolved')}
                  </button>
                </div>
              )}
              {respondFor === f.id && (
                <div style={{ marginTop: 10 }}>
                  <textarea value={responseText} onChange={e => setResponseText(e.target.value)}
                            rows={3} style={{ width: '100%', padding: 8, background: 'var(--card2)',
                                              border: '1px solid var(--border)', color: 'var(--text)' }}
                            placeholder={t('fbResponsePlh')} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <button onClick={() => respond(f.id)}
                            style={{ padding: '6px 14px', background: '#B6FF3C', color: '#000',
                                     border: 0, fontWeight: 700, cursor: 'pointer' }}>
                      {t('fbSend')}
                    </button>
                    <button onClick={() => { setRespondFor(null); setResponseText(''); }}
                            style={{ padding: '6px 14px', background: 'transparent',
                                     border: '1px solid var(--border)', color: 'var(--text)', cursor: 'pointer' }}>
                      {t('fbCancelReply')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
       }
    </div>
  );
}

function Stat({ title, value, color }) {
  return (
    <div style={{ padding: 14, background: 'var(--card)', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--text)', marginTop: 4 }}>{value ?? 0}</div>
    </div>
  );
}
